import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from '../../../api/global.service';
import { NgxFileDropEntry, FileSystemFileEntry, FileSystemDirectoryEntry } from 'ngx-file-drop';
import { CreateFolderPage } from 'src/app/modals/files/create-folder/create-folder.page';
import { CommentPage } from 'src/app/modals/files/comment/comment.page';
import { FilePage } from 'src/app/pop-overs/files/file/file.page';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import { FileEditorPage } from 'src/app/modals/file-editor/file-editor.page';
import { CookieService } from 'ngx-cookie-service';
import * as dayjs from 'dayjs';
import { HomeDetailModalComponent } from 'src/app/home/home-detail-modal/home-detail-modal.component';

@Component({
  selector: 'app-file-explorer',
  templateUrl: './explorer.component.html',
  styleUrls: ['./explorer.component.scss'],
})
export class ExplorerComponent implements OnInit, OnDestroy {

  //This component only takes in the root path that it should use as home
  @Input() root_folder_guid: any = null;
  @Input() allow_hash_change = true;

  // Picker modes: when presented as a modal via ModalController for choosing files/a folder
  // (replaces the old standalone FileSelectModalPage / FolderSelectModalPage components) rather
  // than being embedded as a normal browsing view. Mutually exclusive - folder_select_mode wins
  // if both are somehow set.
  @Input() file_select_mode = false;
  @Input() folder_select_mode = false;
  // Only used by folder_select_mode, to label the confirm button ("Move Here" vs "Copy Here").
  @Input() move = false;

  // Optional, file_select_mode only: identifies the contact/company the caller is actually linked
  // to (e.g. the email being edited), so the picker can warn when the user browses into a
  // different contact's/company's files before picking one. linked_entity_name is just for display.
  @Input() linked_client_guid: any = null;
  @Input() linked_company_guid: any = null;
  @Input() linked_entity_name: any = null;

  // Defaults true (normal browsing shows everything, same as always). Set false by callers where
  // picking a hidden (Internal Only, visible_to_client=0) file wouldn't make sense - e.g. attaching
  // a file to an email the client will receive (see SelectExistingFile in secure-email/details and
  // email-files-prompt). Filtered client-side in SortedData rather than passed to the backend query,
  // since this is a picker-mode UX restriction, not a permissions boundary - the accountant is still
  // allowed to see everything, just not offered hidden files as a choice here.
  @Input() show_hidden_to_client = true;

  // True once the user has browsed into a client/company folder tree that isn't the one this
  // picker is linked to - current_folder carries client_guid/company_guid inherited from whichever
  // entity's tree it lives under (see IsRootFolder), so this is a simple mismatch check. The
  // synthetic "Important" flat view (see GetImportantFiles) doesn't carry those directly - its
  // entity lives on current_folder.source_folder instead. Home ('0', no client_guid/company_guid)
  // isn't "different" - there's nothing to compare yet.
  get IsBrowsingDifferentLinkedEntity(): boolean {
    if (!this.file_select_mode || (!this.linked_client_guid && !this.linked_company_guid)) return false;
    const entity_folder = this.current_folder?.is_important_view ? this.current_folder.source_folder : this.current_folder;
    const folder_client = entity_folder?.client_guid;
    const folder_company = entity_folder?.company_guid;
    if (!folder_client && !folder_company) return false;
    const same_client = this.linked_client_guid && folder_client === this.linked_client_guid;
    const same_company = this.linked_company_guid && folder_company === this.linked_company_guid;
    return !same_client && !same_company;
  }

  get isModalMode(): boolean {
    return this.file_select_mode || this.folder_select_mode;
  }
  private _hide_empty_folders = false;
  get hide_empty_folders() { return this._hide_empty_folders; }
  set hide_empty_folders(v: boolean) { this._hide_empty_folders = v; this.UpdateViewCookies(); }

  private _list_view = false;
  get list_view() { return this._list_view; }
  set list_view(v: boolean) { this._list_view = v; this.UpdateViewCookies(); }

  data: any = null;
  current_folder: any = {
    guid: '0'
  };
  current_guid = '';
  // Also doubles as the "these are the files/folders being moved or copied" list when opened as a
  // folder_select_mode picker - the caller (MoveSelected/CopySelected) seeds it so those rows can
  // be highlighted while the user browses to a destination.
  @Input() selected_files: any = [];
  viewing_important_folder = false;

  nav_history = [];
  loaded_breadcrumbs = false;
  contacts: any = [];
  companies: any = [];
  current_user = null;
  private _show_all_contacts = false;
  get show_all_contacts() { return this._show_all_contacts; }
  set show_all_contacts(v: boolean) { this._show_all_contacts = v; this.UpdateViewCookies(); }

  private _show_only_active = false;
  get show_only_active() { return this._show_only_active; }
  set show_only_active(v: boolean) { this._show_only_active = v; this.UpdateViewCookies(); }

  finished_loading = false;
  // Folder listings are paged (100 at a time) so folders with thousands of items don't block the
  // initial render - subsequent pages load in the background via LoadRemainingPages(). Not used for
  // the Important flat view.
  private readonly PAGE_SIZE = 500;
  private load_generation = 0;
  loading_more_pages = false;

  private _sort_alpha: 'none' | 'asc' | 'desc' = 'none';
  get sort_alpha(): 'none' | 'asc' | 'desc' { return this._sort_alpha; }
  set sort_alpha(v: 'none' | 'asc' | 'desc') { this._sort_alpha = v; if (v !== 'none') this._sort_date = 'none'; this.UpdateViewCookies(); }

  // 'desc' = most recently uploaded first. Mutually exclusive with sort_alpha - picking one clears the other.
  private _sort_date: 'none' | 'asc' | 'desc' = 'none';
  get sort_date(): 'none' | 'asc' | 'desc' { return this._sort_date; }
  set sort_date(v: 'none' | 'asc' | 'desc') { this._sort_date = v; if (v !== 'none') this._sort_alpha = 'none'; this.UpdateViewCookies(); }

  // Files carry their own upload date (created_date); folders don't get uploaded themselves, so
  // last_change (maintained by ChangeFileSystemCountAsync, bubbled up from every file underneath
  // them) stands in as "most recent upload activity in this folder".
  private GetLastUploadedTime(item: any): number {
    const raw = item.is_file ? item.created_date : (item.last_change || item.created_date);
    const parsed = raw ? new Date(raw).getTime() : 0;
    return isNaN(parsed) ? 0 : parsed;
  }

  // Quick search - only ever filters what's already loaded for the folder currently being browsed
  // (this.data), not the wider file tree, hence the "only searching the current folder" note in the UI.
  quickSearchTerm = '';

  onQuickSearchChange(term: string) {
    this.quickSearchTerm = term;
  }

  private MatchesQuickSearch(item: any): boolean {
    const query = this.quickSearchTerm?.trim().toLowerCase();
    if (!query) return true;
    const folder_name = item.is_file ? '' : (item.folder_title || this.globalService.GetPathName(item.path) || '');
    const values = [folder_name, item.file_name, item.client?.display_name, item.company?.name];
    return values.some(value => `${value ?? ''}`.toLowerCase().includes(query));
  }

  get SortedData(): any[] {
    if (!this.data) return [];
    // The synthetic "Important" tile always stays pinned first, regardless of sort mode or search.
    const important = this.data.filter(item => item.is_important_folder);
    const rest = this.data.filter(item => !item.is_important_folder && this.MatchesQuickSearch(item) && (this.show_hidden_to_client || this.IsFolderVisibleToClient(item)));
    if (this.sort_date !== 'none') {
      const sorted_rest = [...rest].sort((a, b) => {
        const diff = this.GetLastUploadedTime(a) - this.GetLastUploadedTime(b);
        return this.sort_date === 'asc' ? diff : -diff;
      });
      return [...important, ...sorted_rest];
    }
    if (this.sort_alpha === 'none') return [...important, ...rest];
    const sorted_rest = [...rest].sort((a, b) => {
      const nameA = (a.is_file ? a.file_name : (a.folder_title || this.globalService.GetPathName(a.path) || '')).toLowerCase();
      const nameB = (b.is_file ? b.file_name : (b.folder_title || this.globalService.GetPathName(b.path) || '')).toLowerCase();
      return this.sort_alpha === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
    return [...important, ...sorted_rest];
  }

  // ── "Recently uploaded" stat, scoped to whatever folder is currently open ──────────────────────

  file_uploaded_count = 0;
  upload_loading = true;
  readonly stat_card_hour_options: { hours: number; label: string; text: string }[] = [
    { hours: 1, label: 'Last Hour', text: 'last hour' },
    { hours: 8, label: 'Last 8 Hours', text: 'last 8 hours' },
    { hours: 24, label: 'Last Day', text: 'last 24 hours' },
    { hours: 48, label: 'Last 2 Days', text: 'last 48 hours' },
    { hours: 72, label: 'Last 3 Days', text: 'last 72 hours' },
    { hours: 168, label: 'Last 7 Days', text: 'last 7 days' },
    { hours: 720, label: 'Last Month', text: 'last month' }
  ];
  upload_hours = 48;
  upload_view_filter: 'all' | 'client' = 'client';

  GetHourRangeText(hours: number): string {
    const opt = this.stat_card_hour_options.find(o => o.hours == hours);
    return opt ? opt.text : `last ${hours} hours`;
  }

  private BuildSinceDate(hours: number): string {
    return dayjs().subtract(hours, 'hour').toISOString();
  }

  // Reuses the same "get_files" recent-uploads mode the home page card uses, scoped to whatever
  // folder is currently open (count=1 so it stays a SQL-level COUNT(*) instead of fetching rows).
  async GetUploadCount() {
    const creator_type_param = this.upload_view_filter === 'client' ? '&creator_type=2' : '';
    const data: any = await this.apiService.get(`files/${this.current_guid}?count=1${creator_type_param}&created_date=${this.BuildSinceDate(this.upload_hours)}`);
    if (data.success && data.data && data.data[0]) this.file_uploaded_count = data.data[0].count;
    this.upload_loading = false;
  }

  OnUploadHoursChange() {
    this.UpdateViewCookies();
    this.GetUploadCount();
  }

  OnUploadViewFilterChange(view: 'all' | 'client') {
    if (this.upload_view_filter === view) return;
    this.upload_view_filter = view;
    this.UpdateViewCookies();
    this.GetUploadCount();
  }

  async OpenUploadedModal() {
    const modal = await this.modalController.create({
      component: HomeDetailModalComponent,
      cssClass: 'large-modal',
      componentProps: {
        title: 'Recently Uploaded Files',
        viewType: 'uploads',
        hours: this.upload_hours,
        uploadViewFilter: this.upload_view_filter,
        folderGuid: this.current_guid
      },
      backdropDismiss: false
    });
    await modal.present();
    const { data: result } = await modal.onDidDismiss();
    if ((result?.uploadViewFilter === 'all' || result?.uploadViewFilter === 'client') && result.uploadViewFilter !== this.upload_view_filter) {
      this.upload_view_filter = result.uploadViewFilter;
      this.UpdateViewCookies();
      this.GetUploadCount();
    }
    if (result?.hours !== undefined && result.hours !== this.upload_hours) {
      this.upload_hours = result.hours;
      this.UpdateViewCookies();
      this.GetUploadCount();
    }
  }


  //Dropzone files
  files: NgxFileDropEntry[] = [];

  private fragmentSubscription: Subscription;

  constructor(
    public apiService: ApiService,
    public globalService: GlobalService,
    private popoverController: PopoverController,
    private alertController: AlertController,
    private modalController: ModalController,
    private cookieService: CookieService,
    private route: ActivatedRoute) { }

  ngOnInit() {
    this.RestoreViewCookies();
    this.InitialLoad();
    // Catches navigating to a different folder via the URL fragment (e.g. "View File" on a
    // notification, "View Folder" on the recently uploaded files modal) while this component is
    // already alive - InitialLoad() only reads window.location.hash once, so without this a
    // same-route Router.navigate({fragment}) would silently do nothing. skip(1) drops the initial
    // emission, which InitialLoad() above already handles via window.location.hash - otherwise
    // both would fire a redundant Get() for the same starting folder.
    if (!this.isModalMode && this.allow_hash_change) {
      this.fragmentSubscription = this.route.fragment.pipe(skip(1)).subscribe(fragment => {
        if (fragment && fragment !== this.current_folder?.guid) {
          this.nav_history = [];
          this.Get(fragment);
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.fragmentSubscription) this.fragmentSubscription.unsubscribe();
  }

  UpdateViewCookies() {
    const prefs = {
      hide_empty_folders: this._hide_empty_folders,
      list_view: this._list_view,
      show_all_contacts: this._show_all_contacts,
      show_only_active: this._show_only_active,
      sort_alpha: this._sort_alpha,
      sort_date: this._sort_date,
      upload_hours: this.upload_hours,
      upload_view_filter: this.upload_view_filter
    };
    this.cookieService.set('fileExplorerPrefs', JSON.stringify(prefs), 30, '/');
  }

  RestoreViewCookies() {
    const raw = this.cookieService.get('fileExplorerPrefs');
    if (!raw) return;
    try {
      const prefs = JSON.parse(raw);
      if (prefs.hide_empty_folders !== undefined) this._hide_empty_folders = prefs.hide_empty_folders;
      if (prefs.list_view !== undefined) this._list_view = prefs.list_view;
      if (prefs.show_all_contacts !== undefined) this._show_all_contacts = prefs.show_all_contacts;
      if (prefs.show_only_active !== undefined) this._show_only_active = prefs.show_only_active;
      if (prefs.sort_alpha !== undefined) this._sort_alpha = prefs.sort_alpha;
      if (prefs.sort_date !== undefined) this._sort_date = prefs.sort_date;
      if (prefs.upload_hours !== undefined) this.upload_hours = Number(prefs.upload_hours);
      if (prefs.upload_view_filter !== undefined) this.upload_view_filter = prefs.upload_view_filter;
    } catch (e) {}
  }

  async InitialLoad() {
    console.log("HASH: " + window.location.hash);
    this.current_user = await this.globalService.GetCurrentUser();
    // Picker modals start fresh at whatever root_folder_guid the caller passed in (defaulting to
    // Home) - they must never read from or write to the page's URL hash, since that belongs to
    // whatever normal file explorer view the user opened the picker on top of.
    if (this.root_folder_guid == null) this.root_folder_guid = '0';
    if (!this.isModalMode) {
      if(window.location.hash == '') {
        if(this.allow_hash_change) window.location.hash = this.current_user.firm_guid;
      }
      if (window.location.hash.replace('#', '') && this.root_folder_guid == '0') {
        console.log("set root guid");
        this.root_folder_guid = window.location.hash.replace('#', '');
      }
    }
    this.Get(this.root_folder_guid);
  }


  //Gets the information about the current folder and gets all the first info about the root folder
  async Get(guid) {
        this.upload_loading = true;
    this.finished_loading = false;
    this.loading_more_pages = false;
    this.viewing_important_folder = false;
    this.quickSearchTerm = '';
    this.current_guid = guid;
    this.load_generation++;
    const load_id = this.load_generation;
    let temp_data: any = await this.apiService.get(`files/${guid}?limit=${this.PAGE_SIZE}&offset=0`);
    // A newer Get()/GetImportantFiles() call has since started (e.g. the user navigated again before
    // this one finished) - drop this stale response instead of letting it clobber the current view.
    if (this.load_generation !== load_id) return;
    console.log("TEMP DATA:", temp_data);
    const first_page_full = temp_data.success && Array.isArray(temp_data.data) && temp_data.data.length === this.PAGE_SIZE;
    if (temp_data.success) {
      this.data = temp_data.data;
      if (temp_data.parent && temp_data.parent[0]) {
        this.current_folder = temp_data.parent[0];
      } else {
        console.error('Get(): backend returned no parent folder data for guid', guid, temp_data);
        this.current_folder = { guid: guid };
      }
      // guid can be a placeholder like '0' (meaning "this firm's root") rather than the real
      // guid - resolve current_guid to whatever the backend actually returned so OnHomePage()
      // (and anything else keyed off current_guid) is accurate regardless of what was passed in.
      this.current_guid = this.current_folder.guid;
    }
    else this.globalService.presentToastMessage(temp_data.message, temp_data.success);
    if (this.data == null) this.data = [];
    // Inject a synthetic "Important" folder tile at the root of a client/company's own folder tree -
    // it's not a real file_system row, clicking it switches to the flat GetImportantFiles() view instead
    // of normal folder navigation. Not relevant while picking a destination folder.
    if (this.IsClientOrCompanyRootFolder() && !this.folder_select_mode) {
      const entity_guid = this.current_folder.client_guid || this.current_folder.company_guid;
      this.data.unshift({
        guid: entity_guid,
        is_file: false,
        is_important_folder: true,
        folder_title: 'Important',
        file_count: null
      });
    }
    this.RestoreSelectionState(this.data);
    if (this.allow_hash_change && !this.isModalMode) {
      console.log("Allow hash change", this.allow_hash_change);
      window.location.hash = this.current_folder.guid;
    }


    //If the current item is not in the history, push it
    if (this.nav_history.findIndex(item => item.guid === this.current_folder.guid) == -1)
      this.nav_history.push(this.current_folder);

    if(this.OnHomePage()) {
      //On root page
      await this.GetContacts();
      await this.GetCompanies();

      await this.CombinedData();
    }
    // Doesn't apply while picking a file/folder in a modal - fire-and-forget so it doesn't hold up the listing render.
    if (!this.isModalMode) this.GetUploadCount();
    this.finished_loading = true;
    this.GenerateBreadCrumbs();

    // The first page was full - there may be more, so keep fetching subsequent pages in the
    // background (not blocking finished_loading) until a page comes back short.
    if (first_page_full) {
      this.LoadRemainingPages(guid, load_id);
    }
  }

  // Re-fetches whatever's currently on screen (a normal folder or the flat "Important" view)
  // without disturbing nav_history/breadcrumbs.
  Refresh() {
    if (this.viewing_important_folder) {
      const entity_guid = this.current_guid.startsWith('important:') ? this.current_guid.substring('important:'.length) : this.current_guid;
      this.GetImportantFiles(entity_guid, this.current_folder?.source_folder);
    } else {
      this.Get(this.current_guid);
    }
  }

  // Background-loads subsequent pages (100 at a time) after the first page, for folders with
  // thousands of items - keeps the initial render fast without waiting on the whole folder's
  // contents. Aborts if the user has navigated elsewhere since this load started.
  async LoadRemainingPages(guid: string, load_id: number) {
    this.loading_more_pages = true;
    let offset = this.PAGE_SIZE;
    while (this.load_generation === load_id) {
      let page_data: any = await this.apiService.get(`files/${guid}?limit=${this.PAGE_SIZE}&offset=${offset}`);
      if (this.load_generation !== load_id) break;
      if (!page_data.success || !Array.isArray(page_data.data)) break;
      this.data.push(...page_data.data);
      this.RestoreSelectionState(page_data.data);
      if (page_data.data.length < this.PAGE_SIZE) break;
      offset += this.PAGE_SIZE;
    }
    // Only clear the flag if this is still the active load - if the user navigated elsewhere,
    // that newer Get() call owns loading_more_pages now.
    if (this.load_generation === load_id) this.loading_more_pages = false;
  }

  // True when the current folder IS a client/company's own root folder (their folder guid always
  // matches their own entity guid) - that's the only place the synthetic "Important" tile shows.
  IsClientOrCompanyRootFolder(): boolean {
    return this.IsRootFolder(this.current_folder);
  }

  // True for the folder (any folder object, not just the one currently open) that anchors a
  // client's or company's whole file tree - it can't be moved, copied, or deleted, only browsed
  // into/out of, since the rest of their file structure hangs off it.
  IsRootFolder(folder: any): boolean {
    if (!folder) return false;
    const isRoot = folder.root_folder == 1 || folder.root_folder === '1' || folder.root_folder === true;
    return isRoot && !!(folder.client_guid || folder.company_guid);
  }

  // "Projects", "Emailed Files", and "File Dropbox" are auto-created by the backend directly under
  // every client's/company's root folder (see Users\Clients.cs / Companies.cs, Emails.cs, and
  // ClientSide\Files.cs for File Dropbox) - not every one of these is guaranteed to exist for a
  // given client/company, but when any of them are present they can't be selected/moved/copied/
  // deleted, only browsed into, same as the root folder itself.
  IsDefaultRootFolder(folder: any): boolean {
    if (!folder || folder.is_file) return false;
    return this.IsClientOrCompanyRootFolder()
      && (folder.folder_title === 'Projects' || folder.folder_title === 'Emailed Files' || folder.folder_title === 'File Dropbox');
  }

  // Content of File Dropbox comes from client-portal uploads only - staff can browse/manage what's
  // there, but can't manually add to it from this side, same restriction as the Important view.
  IsInsideFileDropbox(): boolean {
    return this.current_folder?.folder_title === 'File Dropbox';
  }

  UploadAndCreateRestricted(): boolean {
    return this.viewing_important_folder || this.IsInsideFileDropbox();
  }

  // Small badge icon overlaid on top of the regular folder icon - null means no overlay (a normal
  // folder), same idea as the client/company user/building overlays right below this in the template.
  GetFolderOverlayIcon(folder: any): string | null {
    if (folder?.folder_title === 'Projects') return 'fa-diagram-project';
    if (folder?.folder_title === 'Emailed Files') return 'fa-at';
    if (folder?.folder_title === 'File Dropbox') return 'fa-cloud-arrow-up';
    return null;
  }

  // "Visible to client" only means anything for a folder that actually lives under a client/company's
  // own folder tree - a firm-internal folder has no client to hide it from in the first place.
  IsClientScopedFolder(folder: any): boolean {
    return !!(folder?.client_guid || folder?.company_guid);
  }

  // visible_to_client can come back as a real boolean or as a "1"/"0" string depending on the endpoint
  // (see Global.BoolStringToBool's callers in Files.cs for the same 0/1-string convention backend-side).
  IsFolderVisibleToClient(folder: any): boolean {
    return folder?.visible_to_client === true || folder?.visible_to_client === 1 || folder?.visible_to_client === '1';
  }

  // Flat view of every file marked important anywhere under this client/company's folder tree,
  // regardless of which real subfolder it actually lives in. source_folder is the client/company's
  // own root folder object (needed so breadcrumb navigation back into this view works).
  async GetImportantFiles(entity_guid: string, source_folder: any) {
    this.finished_loading = false;
    this.viewing_important_folder = true;
    this.quickSearchTerm = '';
    this.current_guid = 'important:' + entity_guid;
    this.load_generation++;
    const load_id = this.load_generation;
    let temp_data: any = await this.apiService.get(`files/${entity_guid}?important=1`);
    // A newer Get()/GetImportantFiles() call has since started - drop this stale response.
    if (this.load_generation !== load_id) return;
    if (temp_data.success) {
      this.data = temp_data.data || [];
    } else {
      this.data = [];
      this.globalService.presentToastMessage(temp_data.message, temp_data.success);
    }
    this.RestoreSelectionState(this.data);
    this.current_folder = { guid: this.current_guid, folder_title: 'Important', is_important_view: true, source_folder };
    if (this.nav_history.findIndex(item => item.guid === this.current_folder.guid) == -1) {
      this.nav_history.push(this.current_folder);
    }
    this.finished_loading = true;
  }

  // Selection state (`.selected`) only lives on the transient row objects handed back by the API -
  // it isn't a real file_system column. Every time we (re)load rows for a guid that's currently in
  // selected_files (e.g. navigating away from and back to a folder re-fetches brand new objects),
  // that flag has to be manually restored so the row still renders as selected.
  private RestoreSelectionState(rows: any[]) {
    if (!rows || rows.length === 0 || this.selected_files.length === 0) return;
    const selected_guids = new Set(this.selected_files.map((f: any) => f.guid));
    for (const row of rows) {
      if (selected_guids.has(row.guid)) row.selected = true;
    }
  }

  // Files in the Important view only carry their raw path (a chain of GUIDs: firm_guid/entity_guid/...).
  // Swap the firm and client/company GUIDs for their real names so hovering tells the user which
  // client/company the file actually belongs to instead of a meaningless GUID string.
  GetImportantFileTooltip(obj: any): string {
    if (!obj?.path) return '';
    let display_path = obj.path;
    const source_folder = this.current_folder?.source_folder;
    const firm_guid = this.current_user?.firm_guid;
    const firm_name = this.current_user?.firm_name;
    const entity_guid = source_folder?.client_guid || source_folder?.company_guid;
    const entity_name = source_folder?.folder_title;
    if (firm_guid && firm_name) display_path = display_path.split(firm_guid).join(firm_name);
    if (entity_guid && entity_name) display_path = display_path.split(entity_guid).join(entity_name);
    return display_path;
  }

  async CombinedData() {
    for (let file of this.data) {
      file.client = this.contacts.find(contact => contact.guid === file.client_guid);
      file.company = this.companies.find(company => company.guid === file.company_guid);
    }
    console.log("COMBINED DATA:", this.data);
  }

  async GetContacts() {
    this.contacts = [];
    let temp_data: any = await this.apiService.get(`clients`);
    console.log("TEMP DATA:", temp_data);
    if (temp_data.success) {
      this.contacts = temp_data.data;
    }
    if(!this.contacts) this.contacts = [];
  }

  async GetCompanies() {
    this.companies = [];
    let temp_data: any = await this.apiService.get(`companies`);
    console.log("TEMP DATA:", temp_data);
    if (temp_data.success) {
      this.companies = temp_data.data;
    }
    if(!this.companies) this.companies = [];
  }

  async BreadcrumbClick(guid) {
    console.log("GUID: " + guid);
    var index = this.nav_history.findIndex(item => item.guid === guid);
    console.log(index);
    const target = this.nav_history[index];
    this.nav_history.splice(index + 1, this.nav_history.length - (index + 1));
    if (target && target.is_important_view) {
      const source = target.source_folder;
      this.GetImportantFiles(source.client_guid || source.company_guid, source);
    } else {
      this.Get(guid);
    }
  }

  // Jumping here straight from the flat Important view skips all the normal click-by-click
  // navigation that would otherwise build up nav_history - so rebuild the real ancestor chain
  // from scratch (same approach as GenerateBreadCrumbs) before loading the folder.
  async GoToFolder(file: any) {
    if (!file?.parent_folder_guid) return;
    this.loaded_breadcrumbs = false;
    this.nav_history = [];
    let last_parent_guid = file.parent_folder_guid;
    const navigation_root_guid = this.GetNavigationRootGuid();
    while (last_parent_guid) {
      let parent_data: any = await this.apiService.get(`folder/parent/${last_parent_guid}`);
      if (parent_data.data && parent_data.data[0]) {
        this.nav_history.unshift(parent_data.data[0]);
        if (parent_data.data[0].guid === navigation_root_guid) break;
        last_parent_guid = parent_data.data[0].guid;
      } else {
        break;
      }
    }
    this.TrimNavHistoryToNavigationRoot();
    await this.Get(file.parent_folder_guid);
  }

  //When a file is dropped
  async dropped(files: NgxFileDropEntry[]) {
    if (this.UploadAndCreateRestricted()) {
      this.globalService.presentToastMessage("Files can't be uploaded here.", false);
      return;
    }
    this.files = files;
    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {

        console.log("Present Loading");
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        await fileEntry.file(async (file: File) => {
          await this.apiService.UploadFile(file,this.current_folder.guid);
        });
      } else {
        // It was a directory (empty directories are added, otherwise only files)
        const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
      }
    }
    this.Get(this.current_folder.guid);
  }

  //Open the file selector for the dropzone
  openFileSelector() {
    if (this.UploadAndCreateRestricted()) {
      this.globalService.presentToastMessage("Files can't be uploaded here.", false);
      return;
    }
    let element: HTMLElement = document.getElementById("UploadFileButton");
    element.click();
  }

  // Only PDFs get the "Preview" option in the right-click menu - everything else still just downloads.
  IsPdfFile(file: any): boolean {
    if (!file?.is_file || !file.path) return false;
    return (this.globalService.GetFileType(file.path) || '').toLowerCase() === 'pdf';
  }

  //Presents the pop over for extra file options
  async presentFilePopover(e, file) {
    e.stopPropagation();
    e.preventDefault();
    const popover = await this.popoverController.create({
      component: FilePage,
      componentProps: {
        file: file,
        selected_files: this.selected_files,
        // GoToFolder works off file.parent_folder_guid alone, so it's meaningful for any file in the
        // explorer's context menu, not just the flat "Important" view it was originally built for.
        show_go_to_folder: true,
        is_pdf: this.IsPdfFile(file)
      },
      event: e,
      animated: true,
      backdropDismiss: true
    });
    popover.onDidDismiss()
      .then((data) => {
        console.log(data);
        // 0 = select file
        // 1 = share file
        // 2 = Move file
        // 3 = Copy file
        // 4 = Download file
        // 5 = comment
        // 6 = preview
        // 7 = delete
        // 8 = toggle important
        // 9 = go to folder
        // null = cancel
        if (data.data == 0) this.SelectFile(e, file);
        else if (data.data == 1) { this.UnselectAll(); this.SelectFile(e, file); this.ShareSelected(); }
        else if (data.data == 2) { this.UnselectAll(); this.SelectFile(e, file); this.MoveSelected(); }
        else if (data.data == 3) { this.UnselectAll(); this.SelectFile(e, file); this.CopySelected(); }
        else if (data.data == 4) this.apiService.DownloadFile(file);
        else if (data.data == 5) this.CommentOnFile(file);
        else if (data.data == 7) this.DeleteFile(file);
        else if (data.data == 8) this.ToggleImportant(file);
        else if (data.data == 9) this.GoToFolder(file);

      });
    return await popover.present();
  }

  async ToggleImportant(file: any) {
    const new_value = !file.is_important;
    const data: any = await this.apiService.put(`files/${file.guid}/important`, { important: new_value });
    if (data.success) {
      file.is_important = new_value;
      this.globalService.presentToastMessage(new_value ? 'Marked as important' : 'Removed from important', true);
    } else {
      this.globalService.presentToastMessage(data.message, false);
    }
  }

  //File options

  //Select and deselect a file, this is called on right click or pop-over option
  async SelectFile(e, file) {
    e.stopPropagation();
    e.preventDefault();
    // Folder-picker mode has no concept of "selecting" a row - the folder you're currently
    // browsing IS the selection. File-picker mode only lets you select files, not folders.
    if (this.folder_select_mode) return false;
    if (this.file_select_mode && !file.is_file) return false;
    // A client/company's root folder anchors their whole file tree - it can be browsed into, but
    // never selected for the bulk Move/Copy/Delete actions.
    if (!file.is_file && this.IsRootFolder(file)) {
      this.globalService.presentToastMessage("This folder can't be moved, copied, or deleted.", false);
      return false;
    }
    if (this.IsDefaultRootFolder(file)) {
      this.globalService.presentToastMessage("This folder can't be moved, copied, or deleted.", false);
      return false;
    }
    file.selected = !file.selected;
    if (file.selected) {
      this.selected_files.push(file);
    }
    else {
      var index = this.selected_files.findIndex(item => item.guid === file.guid);
      if (index > -1)
        this.selected_files.splice(index, 1);
    }
    return false;
  }

  // Routes a normal-mode click (download) vs file_select_mode (toggle selection) vs
  // folder_select_mode (no action at all - files are shown for context only while picking a
  // destination folder, but aren't interactive).
  OnFileClicked(e, file) {
    if (this.folder_select_mode) return;
    if (this.file_select_mode) { this.SelectFile(e, file); return; }
    this.apiService.DownloadFile(file);
  }

  // Same idea for right-click: normal mode opens the file's action popover, file_select_mode
  // toggles selection, folder_select_mode does nothing.
  OnFileContextMenu(e, file) {
    if (this.folder_select_mode) return;
    if (this.file_select_mode) { this.SelectFile(e, file); return; }
    this.presentFilePopover(e, file);
  }

  // While picking a destination folder, highlights whichever files/folders are actually being
  // moved/copied (passed in via selected_files) if they happen to be visible in the folder
  // you're currently browsing - so it's obvious you haven't lost them along the way.
  IsFileBeingMoved(item: any): boolean {
    return this.folder_select_mode && this.selected_files.some((f: any) => f.guid === item.guid);
  }

  async ShareFile(file) {

  }

  //Move the file and then update the file / view. Returns null if the file is already in the directory we are trying to move it too
  async MoveFile(file, folder_guid) {
    if (file.parent_folder_guid != folder_guid) {
      var put_data = {
        folder_guid: folder_guid
      }
      await this.globalService.presentLoading(`Moving ${file.file_name}`, 30000);
      let data: any = await this.apiService.put(`files/move/${file.guid}`, put_data);
      //If its successful, we need to see if we need to remove it from the current directory
      if (data.success) {
        //Update the file to have the new path
        file = data.data[0];
        var index = this.data.findIndex(item => item.guid === file.guid);
        if (file.parent_folder_guid != this.current_folder.guid) {
          this.data.splice(index, 1);
        } else if (file.parent_folder_guid == this.current_folder.guid && index == -1) {
          this.data.push(file);
        }
      }
      //Dismiss loading and show the message
      this.globalService.dismissLoading();
      this.globalService.presentToastMessage(data.message, data.success);

      return data;
    }
    return null;
  }

  //Copy the file, then add it to the current view if its supposed to be
  async CopyFile(file, folder_guid) {
    var put_data = {
      folder_guid: folder_guid
    }
    await this.globalService.presentLoading(`Copying ${file.file_name}`, 30000);
    let data: any = await this.apiService.post(`files/copy/${file.guid}`, put_data);
    //If its successful, we need to see if we need to remove it from the current directory
    if (data.success) {
      let new_file = data.data[0];
      if (new_file.parent_folder_guid == this.current_folder.guid) {
        this.data.push(new_file);
      }
    }
    //Dismiss loading and show the message
    this.globalService.dismissLoading();
    this.globalService.presentToastMessage(data.message, data.success);
    return data;

  }

  async CommentOnFile(file) {
    const modal = await this.modalController.create({
      component: CommentPage,
      componentProps: {
        file: file,
      },
      backdropDismiss: true,
      cssClass: 'large-modal secondary-modal'
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
  }

  //Delete a file
  async DeleteFile(file) {
    //Remove this ID from the current array
    let data: any = await this.apiService.delete(`files/${file.guid}`, null);
    if (data.success) {
      var index = this.data.findIndex(item => item.guid === file.guid);
      this.data.splice(index, 1);
    }
    this.globalService.presentToastMessage(data.message, data.success);
  }


  //Options for selected files
  async DownloadSelected() {
    let guid_array = [];
    for (const file of this.selected_files) {
      guid_array.push(file.guid);
    }
    this.apiService.DownloadAsZip(guid_array);
  }

  async EditSelected() {
    const modal = await this.modalController.create({
      component: FileEditorPage,
      componentProps: {
       file: this.selected_files[0]
      },
      cssClass: 'large-modal',
      backdropDismiss: false,
    });


    modal.present();

    const { data, role } = await modal.onWillDismiss();
    //Data is expected to return a create folder object that is
    // name, visible_to_client
    if (data) {
      // for (let file of this.selected_files) {
      //   await this.MoveFile(file, data.guid);
      // }
      // this.UnselectAll();

    }
  }

  async ShareSelected() {

  }


  async MoveSelected() {
    const modal = await this.modalController.create({
      component: ExplorerComponent,
      componentProps: {
        folder_select_mode: true,
        move: true,
        selected_files: this.selected_files,
        root_folder_guid: this.GetFolderPickerStartFolder()?.guid || '0'
      },
      cssClass: 'large-modal',
      backdropDismiss: false,
    });


    modal.present();

    const { data, role } = await modal.onWillDismiss();
    //Data is expected to return a create folder object that is
    // name, visible_to_client
    if (data) {
      for (let file of this.selected_files) {
        await this.MoveFile(file, data.guid);
      }
      this.UnselectAll();

    }
  }

  async CopySelected() {
    const modal = await this.modalController.create({
      component: ExplorerComponent,
      componentProps: {
        folder_select_mode: true,
        move: false,
        selected_files: this.selected_files,
        root_folder_guid: this.GetFolderPickerStartFolder()?.guid || '0'
      },
      cssClass: 'large-modal',
      backdropDismiss: false,
    });


    modal.present();

    const { data, role } = await modal.onWillDismiss();
    //Data is expected to return a create folder object that is
    // name, visible_to_client
    if (data) {
      //Loop through each selected file and move it
      for (let file of this.selected_files) {
        await this.CopyFile(file, data.guid);
      }
      this.UnselectAll();
    }
  }

  // The folder picker (move/copy) needs a REAL folder guid to start browsing from. While viewing the
  // flat "Important" view, current_folder.guid is the synthetic "important:<entity_guid>" string, not
  // a real folder - passing that straight through makes the picker fetch `files/important:<guid>`,
  // which the backend can't resolve and rejects as unauthorized. Fall back to the entity's real root
  // folder (source_folder) in that case.
  GetFolderPickerStartFolder(): any {
    if (this.current_folder?.is_important_view) {
      return this.current_folder.source_folder || null;
    }
    return this.current_folder;
  }

  // Picker-mode footer actions (file_select_mode / folder_select_mode) - dismiss the modal with
  // whatever the caller is expecting back, matching what the old FileSelectModalPage /
  // FolderSelectModalPage used to dismiss with.
  ConfirmFileSelection() {
    this.modalController.dismiss(this.selected_files);
  }

  ConfirmFolderSelection() {
    this.modalController.dismiss(this.current_folder);
  }

  CancelSelectMode() {
    this.modalController.dismiss(null);
  }

  // The ✕ on a badge in the "Selected" list. The row in the current folder can be a different object
  // than the one in selected_files (folders are re-fetched when browsed back into), so match by guid.
  RemoveFromSelection(item: any) {
    item.selected = false;
    const row = (this.data || []).find((row: any) => row.guid === item.guid);
    if (row) row.selected = false;
    this.selected_files = this.selected_files.filter((selected: any) => selected.guid !== item.guid);
  }

  async UnselectAll() {
    for (const file of this.data) {
      file.selected = false;
    }
    // Items picked in other folders aren't in this.data - clear their flag too
    for (const file of this.selected_files) {
      file.selected = false;
    }
    this.selected_files = [];
  }

  SelectAllFiles() {
    for (const item of this.data) {
      if (item.is_file && !item.selected) {
        item.selected = true;
        this.selected_files.push(item);
      }
    }
  }

  SelectAllFolders() {
    for (const item of this.data) {
      if (!item.is_file && !item.selected && !this.ShouldHideFolder(item) && !this.IsRootFolder(item) && !this.IsDefaultRootFolder(item)) {
        item.selected = true;
        this.selected_files.push(item);
      }
    }
  }

  //Delete all selected files, then empty the selected array
  async DeleteSelected() {
    console.log("STart Delete");
    for (const file of this.selected_files) {
      await this.globalService.presentLoading(`Deleting ${file.file_name}`, 30000);
      await this.DeleteFile(file);
      this.globalService.dismissLoading();
    }
    console.log("Finish Delete");
    this.selected_files = [];
  }

  async PresentDeleteSelectedConfirm() {
    let file_list_html = "";
    for (const file of this.selected_files) {
      file_list_html += `<br> ${file.file_name}`
    }
    const alert = await this.alertController.create({
      backdropDismiss: false,
      header: 'Are you sure you would like to delete!',
      message: `${file_list_html}`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            console.log("Cancel")
          },
        },
        {
          text: 'OK',
          role: 'confirm',
          handler: () => {
            this.DeleteSelected();
          },
        },
      ],
    });

    await alert.present();
  }


  //MODALs
  async OpenCreateFolderModal() {
    if (this.UploadAndCreateRestricted()) {
      this.globalService.presentToastMessage("Folders can't be created here.", false);
      return;
    }
    const modal = await this.modalController.create({
      component: CreateFolderPage,
      componentProps: {
        current_folder: this.current_folder
      },
      backdropDismiss: false,
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    //Data is expected to return a create folder object that is
    // name, visible_to_client
    if (data) {

      this.data.push(data[0]);
    }
  }

  // Rebuilds the ancestor chain for the breadcrumb trail whenever nav_history doesn't already
  // represent it - true on any "cold start" below Home: a hash-loaded page refresh, or a
  // file_select_mode/folder_select_mode picker opening directly at the folder the user was
  // already browsing. Normal click-by-click navigation builds nav_history up incrementally
  // (Get() appends one entry per click) so it's already correct and this is a no-op then.
  async GenerateBreadCrumbs() {
    const navigation_root_guid = this.GetNavigationRootGuid();
    if (this.nav_history.length <= 1 && this.current_folder.guid !== navigation_root_guid) {
      let last_parent_guid = this.current_folder.guid;
      while (last_parent_guid) {
        let parent_data: any = await this.apiService.get(`folder/parent/${last_parent_guid}`);
        if (parent_data.data && parent_data.data[0]) {
          this.nav_history.unshift(parent_data.data[0]);
          if (parent_data.data[0].guid === navigation_root_guid) break;
          last_parent_guid = parent_data.data[0].guid;
        }
        else {
          console.log("BREAK");
          break;
        }
      }
    }
    this.TrimNavHistoryToNavigationRoot();
    this.loaded_breadcrumbs = true;
  }

  async CreateDocument() {
    console.log(this.current_folder.guid);
    const modal = await this.modalController.create({
      component: FileEditorPage,
      componentProps: {
       create_file: true,
       current_folder_guid: this.current_folder.guid
      },
      cssClass: 'large-modal',
      backdropDismiss: false,
    });


    modal.present();

    const { data, role } = await modal.onWillDismiss();
    //Data is expected to return a create folder object that is
    // name, visible_to_client
    if (data) {
      // for (let file of this.selected_files) {
      //   await this.MoveFile(file, data.guid);
      // }
      // this.UnselectAll();

    }
  }

  HasScopedRoot(): boolean {
    return !this.isModalMode && !this.allow_hash_change && this.root_folder_guid != null && `${this.root_folder_guid}` !== '0';
  }

  private GetNavigationRootGuid(): string {
    return this.HasScopedRoot() ? `${this.root_folder_guid}` : '0';
  }

  GetRootBreadcrumbLabel(folder: any): string {
    if (!this.HasScopedRoot()) return 'Home';
    return folder?.folder_title || this.globalService.GetPathNameTwo(folder) || 'Files';
  }

  private TrimNavHistoryToNavigationRoot() {
    if (!this.HasScopedRoot() || !this.nav_history?.length) return;
    const root_index = this.nav_history.findIndex((item: any) => item.guid === this.GetNavigationRootGuid());
    if (root_index > 0) this.nav_history.splice(0, root_index);
  }

  getTableDescription(): string {
    const parts: string[] = [];

    if (this.OnHomePage()) {
      parts.push(this.show_all_contacts ? 'showing all contacts and companies' : 'showing clients only');
      if (this.show_only_active) parts.push('active portal access only');
    }

    if (this.hide_empty_folders || this.file_select_mode) parts.push('empty folders hidden');

    if (this.sort_alpha === 'asc') parts.push('sorted A\u2013Z');
    else if (this.sort_alpha === 'desc') parts.push('sorted Z\u2013A');
    else if (this.sort_date === 'desc') parts.push('sorted by newest upload');
    else if (this.sort_date === 'asc') parts.push('sorted by oldest upload');

    parts.push(this.list_view ? 'list view' : 'grid view');

    if (this.quickSearchTerm?.trim()) parts.push(`quick searching for: ${this.quickSearchTerm.trim()}`);

    return parts.length === 0 ? '' : parts[0].charAt(0).toUpperCase() + parts[0].slice(1) + (parts.length > 1 ? ', ' + parts.slice(1).join(', ') : '') + '.';
  }

  OnHomePage() {
    if(this.current_user == null) return false;
    if(this.current_guid == '') return false;
    return this.current_guid == this.current_user.firm_guid;
  }

  ShouldHideFolder(folder) {
    // Picking a file: an empty folder can't possibly contain what you're looking for, so always
    // hide it there - regardless of the hide_empty_folders toggle/cookie state (its toolbar isn't
    // even shown in this mode).
    if (this.file_select_mode && folder.file_count == 0) return true;

    // The client/company-type filtering below (show_all_contacts / show_only_active) only makes
    // sense for the flat list of client/company root folders shown on the Home page - it doesn't
    // apply once you're browsing inside a normal folder tree.
    if (this.OnHomePage() && this.contacts != null && this.contacts.length > 0 && this.companies != null && this.companies.length > 0) {
      if(folder.client) {
        if(!folder.client.is_a_client && !this.show_all_contacts) return true;
        else if (this.show_only_active && !folder.client.active) return true;
      }
      else if (folder.company) {
        if(!folder.company.is_a_client && !this.show_all_contacts) return true;
        else if (this.show_only_active) return true;
      }
    }

    // Unlike the block above, "hide empty folders" applies everywhere, not just on the Home page.
    if(this.hide_empty_folders && folder.file_count == 0) return true;

    return false;

  }



}
